// Shared helpers for surfacing a crew's SUBMITTED photos in review screens —
// the wizard's "view submission" (SMRTCOP.tsx) and the admin's "view site"
// (ManageSMRTCOP.tsx). Photos are keyed at wizard RUNTIME from the site's
// checklist (`${photoSectionId}_photo_…` for Sector A/B/C + SPD, plus
// crew-added `…__extra__<id>` slots), so neither screen can rely on its static
// template questions — both discover photos directly from the submitted data.
//
// Storage shape: the filename lives at the key, the Azure blob name at
// `${key}_blob` (legacy sites used `${key}_dataUrl`).

// A submitted key holds a photo when its value is a filename string and it has
// a sibling blob/dataUrl pointer. Report slots store an object (not a string),
// and plain text answers have no blob sibling — both are naturally excluded.
export const isSubmittedPhoto = (
  data: Record<string, any>,
  k: string,
): boolean =>
  typeof data[k] === "string" &&
  !!data[k] &&
  (data[`${k}_blob`] !== undefined || data[`${k}_dataUrl`] !== undefined);

export interface PhotoGroup {
  label: string;
  keys: string[];
}

const SECTOR_ORDER = ["A", "B", "C", "SPD"];

// Display labels for each sector group header. Ericsson names sectors
// Alpha/Beta/Gamma (A/B/C); show both so crews and admins recognise either.
const SECTOR_LABEL: Record<string, string> = {
  A: "Sector Alpha (A)",
  B: "Sector Beta (B)",
  C: "Sector Gamma (C)",
  SPD: "SPD",
};

// Photos live under two key shapes depending on checklist format: legacy
// `${sectionId}_photo_{A|B|C|SPD}_…` and the new Ericsson v2
// `${sectionId}_v2_{A|B|C}_{categoryId}_…`. Both belong to the SAME template
// photo section (keys are prefixed with the ORIGIN section id), so review
// screens must match either — otherwise v2 photos slip through to the
// "Additional Photos" catch-all.
const photoPrefixes = (sectionId: number | string): string[] => [
  `${sectionId}_photo_`,
  `${sectionId}_v2_`,
];

// True when the submitted data carries any photo for the given section id.
export const sectionHasPhotos = (
  data: Record<string, any>,
  sectionId: number | string,
): boolean => {
  const prefixes = photoPrefixes(sectionId);
  return Object.keys(data).some((k) => prefixes.some((p) => k.startsWith(p)));
};

// Collect a section's photo keys, grouped by the sector/SPD token that follows
// the prefix, in A → B → C → SPD order. The token sits right after `_photo_`
// (legacy) or `_v2_` (v2, always a sector letter) — uppercased so both formats
// bucket into the same Sector A/B/C group.
export function groupSectionPhotos(
  data: Record<string, any>,
  sectionId: number | string,
): PhotoGroup[] {
  const legacyPrefix = `${sectionId}_photo_`;
  const v2Prefix = `${sectionId}_v2_`;
  const keys = Object.keys(data)
    .filter(
      (k) =>
        (k.startsWith(legacyPrefix) || k.startsWith(v2Prefix)) &&
        isSubmittedPhoto(data, k),
    )
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  // Bucket by the raw sector/SPD token so ordering + labelling stay reliable
  // (labels like "Sector Alpha (A)" can't be reverse-parsed back to a token).
  const groups = new Map<string, string[]>();
  for (const k of keys) {
    const raw = k.startsWith(v2Prefix)
      ? k.slice(v2Prefix.length).split("_")[0].toUpperCase()
      : k.slice(legacyPrefix.length).split("_")[0];
    const token = ["A", "B", "C", "SPD"].includes(raw) ? raw : "Other";
    if (!groups.has(token)) groups.set(token, []);
    groups.get(token)!.push(k);
  }

  return [...groups.entries()]
    .sort(
      ([a], [b]) =>
        (SECTOR_ORDER.indexOf(a) + 1 || 99) -
        (SECTOR_ORDER.indexOf(b) + 1 || 99),
    )
    .map(([token, ks]) => ({ label: SECTOR_LABEL[token] || "Other", keys: ks }));
}

// The sector/SPD token carried by a photo key, or "" when it has none.
// Matches both key shapes groupSectionPhotos handles — legacy
// `${sectionId}_photo_{A|B|C|SPD}_…` and v2 `${sectionId}_v2_{A|B|C}_…` —
// without needing the section id, so download code can read it off a bare key.
//
// This is the ONLY place the sector survives on a v2 photo: its filename is
// built from the category label alone (buildFilename in SMRTCOP.tsx), which is
// identical across Alpha/Beta/Gamma. See downloadFileName in photoDownload.ts.
const SECTOR_TOKEN_RE = /_(?:v2|photo)_([A-Za-z]+?)(?:_|$)/;

export function sectorTokenForKey(key: string): string {
  const m = SECTOR_TOKEN_RE.exec(key || "");
  if (!m) return "";
  const raw = m[1].toUpperCase();
  return ["A", "B", "C", "SPD"].includes(raw) ? raw : "";
}

// Any photo not already rendered under a section (safety net — e.g. inline
// question photos, crew-added extras whose section didn't render).
export function leftoverPhotos(
  data: Record<string, any>,
  rendered: Set<string>,
): string[] {
  return Object.keys(data)
    .filter((k) => isSubmittedPhoto(data, k) && !rendered.has(k))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

// Copy a submitted photo to the clipboard as a PNG so it can be pasted
// straight into the Ericsson portal. Browsers only reliably accept image/png
// on the clipboard (not JPEG), so we re-encode via a canvas. The ClipboardItem
// is handed a Promise<Blob> synchronously so Safari keeps the user-gesture
// while the fetch/convert runs. Throws if the clipboard API is unavailable
// (e.g. non-secure context) or the fetch/convert fails — callers surface that.
export async function copyPhotoToClipboard(
  siteId: string,
  fieldKey: string,
  blobName: string,
): Promise<void> {
  if (!navigator.clipboard || typeof ClipboardItem === "undefined") {
    throw new Error("Clipboard image copy not supported in this browser");
  }
  const pngBlob = (async () => {
    const r = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/smrtcop/files/${encodeURIComponent(siteId)}/${encodeURIComponent(fieldKey)}?blobName=${encodeURIComponent(blobName)}&format=base64`,
      { credentials: "include" },
    );
    if (!r.ok) throw new Error(`copy ${r.status}`);
    const data = await r.json();
    const binary = atob(data.base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const bitmap = await createImageBitmap(
      new Blob([bytes], { type: data.contentType || "image/jpeg" }),
    );
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext("2d")!.drawImage(bitmap, 0, 0);
    bitmap.close?.();
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
        "image/png",
      ),
    );
  })();
  await navigator.clipboard.write([new ClipboardItem({ "image/png": pngBlob })]);
}
